import { useMemo, useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Chip, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowParams,
} from '@mui/x-data-grid';
import { Link as RouterLink } from 'react-router';

import { StatusChip } from '@/components/common/StatusChip';
import type { DeploymentOption, ScheduleSummary } from '@/types/schedule';

import { DAYS_OF_WEEK } from '../utils/cronConstants';
import { getDaysFromCronExpression } from '../utils/cronUtils';
import { formatDuration } from '../utils/dateRanges';
import { formatScheduleApiDateTime } from '../utils/scheduleDateTime';
import {
  formatConfiguredScheduleFrequencyTooltip,
  formatCronScheduleFrequency,
  formatDailyScheduleFrequency,
  formatRepeatingScheduleFrequency,
} from '../utils/scheduleDisplayFormat';
import { type TimeDisplayMode, TimeDisplayToggle } from './shared/TimeDisplayToggle';

/**
 * Returns true for EventBridge Fixed-time crons: 6 fields where the last
 * field is a specific 4-digit year (not '*').
 * Format: "minute hour dom month dow year"  e.g. "0 9 1 5 * 2026"
 */
function isFixedTimeCron(cronExpression?: string): boolean {
  if (!cronExpression) return false;
  const parts = cronExpression.trim().split(/\s+/);
  return parts.length === 6 && /^\d{4}$/.test(parts[5]);
}

function deploymentLookupKey(baseId: string | number, environmentId: string | number): string {
  return `${String(baseId)}|${String(environmentId)}`;
}

interface ScheduleSummaryGridProps {
  schedules: ScheduleSummary[];
  isLoading?: boolean;
  onEdit?: (schedule: ScheduleSummary) => void;
  onRowClick?: (schedule: ScheduleSummary) => void;
  onRefresh?: () => void;
  selectedEnvironmentId?: string;
  appId?: string;
  timeMode?: TimeDisplayMode;
  onTimeModeChange?: (mode: TimeDisplayMode) => void;
  /** When false, hide the grid toolbar toggle (e.g. when page header owns it). */
  showTimeToggle?: boolean;
  /**
   * App deployments used to resolve the version currently deployed per
   * recipe + environment (baseId|environmentId).
   */
  deployments?: DeploymentOption[];
}

// Day of week display — only shows the selected days
const DayOfWeekCell = ({ days }: { days: number[] }) => {
  const selectedDays = DAYS_OF_WEEK.filter((day) => days.includes(parseInt(day.value, 10)));

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {selectedDays.map((day) => (
        <Chip
          key={day.value}
          label={day.label}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ height: 22, fontSize: '0.75rem', '& .MuiChip-label': { px: 0.75 } }}
        />
      ))}
    </Stack>
  );
};

// Status cell renderer
const StatusCell = ({
  status,
  externalExecutionId,
  environmentId,
  appId,
}: {
  status?: string;
  externalExecutionId?: string;
  environmentId?: string;
  appId?: string;
}) => {
  if (!status) {
    return <Typography variant="body2">-</Typography>;
  }

  if (externalExecutionId && appId) {
    return (
      <Link
        href={`/a/${appId}/health?executionGuid=${externalExecutionId}&env=${environmentId}`}
        underline="hover"
      >
        <StatusChip status={status} />
      </Link>
    );
  }

  return <StatusChip status={status} />;
};

export const ScheduleSummaryGrid = ({
  schedules,
  isLoading = false,
  onEdit,
  onRowClick,
  onRefresh,
  selectedEnvironmentId,
  appId,
  timeMode: timeModeProp,
  onTimeModeChange,
  showTimeToggle = true,
  deployments,
}: ScheduleSummaryGridProps) => {
  const [internalTimeMode, setInternalTimeMode] = useState<TimeDisplayMode>('local');
  const timeMode = timeModeProp ?? internalTimeMode;
  const setTimeMode = onTimeModeChange ?? setInternalTimeMode;

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });

  const deployedVersionMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!deployments) return map;
    for (const deployment of deployments) {
      if (!deployment.version) continue;
      map.set(deploymentLookupKey(deployment.baseId, deployment.environmentId), deployment.version);
    }
    return map;
  }, [deployments]);

  // Filter schedules by environment if specified
  const filteredSchedules = selectedEnvironmentId
    ? schedules.filter((s) => String(s.environmentId) === String(selectedEnvironmentId))
    : schedules;

  // Enrich schedules with computed fields
  const enrichedSchedules = filteredSchedules.map((schedule) => {
    let days: number[] = schedule.days || [];
    if (!days.length && schedule.cronExpression) {
      days = getDaysFromCronExpression(schedule.cronExpression);
    }

    let frequency = '';
    if (schedule.dailySchedule) {
      frequency = formatDailyScheduleFrequency(
        schedule.dailySchedule.timeUTC,
        schedule.dailySchedule.timeZone,
        timeMode
      );
    } else if (schedule.repeatingSchedule) {
      frequency = formatRepeatingScheduleFrequency(schedule.repeatingSchedule, timeMode);
    } else if (schedule.cronExplain || schedule.cronExpression) {
      frequency = formatCronScheduleFrequency(
        schedule.cronExpression || '',
        schedule.cronExplain,
        schedule.cronTimeZone,
        timeMode,
        undefined,
        schedule.cronTimeFrame
      );
    } else if (schedule.frequency) {
      frequency = schedule.frequency;
    }

    const deployedVersion = deployedVersionMap.get(
      deploymentLookupKey(schedule.baseId, schedule.environmentId)
    );

    return {
      ...schedule,
      id: `${schedule.baseId}-${schedule.environmentId}`,
      days,
      frequency,
      frequencyTooltip: formatConfiguredScheduleFrequencyTooltip(schedule),
      isFixedTime: isFixedTimeCron(schedule.cronExpression),
      deployedVersion,
    };
  });

  const columns: GridColDef[] = [
    {
      field: 'transformerName',
      headerName: 'Recipe',
      flex: 1,
      minWidth: 180,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ScheduleSummary & { deployedVersion?: string };
        const recipeName = params.value as string | undefined;
        if (!recipeName) {
          return (
            <Typography variant="body2" noWrap>
              -
            </Typography>
          );
        }
        const versionLabel = row.deployedVersion ? `(v${row.deployedVersion})` : null;
        const displayLabel = versionLabel ? `${recipeName} · ${versionLabel}` : recipeName;
        const tooltipTitle = versionLabel
          ? `${recipeName} (${versionLabel} deployed to ${row.environmentName}) — runs on next schedule`
          : `${recipeName} — open in Recipe Editor`;

        if (!appId) {
          return (
            <Tooltip title={tooltipTitle}>
              <Typography variant="body2" noWrap>
                {displayLabel}
              </Typography>
            </Tooltip>
          );
        }
        const editorPath = `/a/${appId}/tf/${row.baseId}`;
        return (
          <Tooltip title={tooltipTitle}>
            <Link
              component={RouterLink}
              to={editorPath}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              onClick={(e) => e.stopPropagation()}
              sx={{ display: 'block', minWidth: 0, fontWeight: 500 }}
            >
              <Typography variant="body2" color="primary" noWrap component="span">
                {recipeName}
                {versionLabel && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 400 }}
                  >
                    {` · ${versionLabel}`}
                  </Typography>
                )}
              </Typography>
            </Link>
          </Tooltip>
        );
      },
    },
    {
      field: 'environmentName',
      headerName: 'Environment',
      width: 140,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value} size="small" color="secondary" variant="outlined" />
      ),
    },
    {
      field: 'days',
      headerName: 'Days',
      width: 220,
      sortable: false,
      display: 'flex',
      renderCell: (
        params: GridRenderCellParams<ScheduleSummary & { days: number[]; isFixedTime: boolean }>
      ) => {
        const row = params.row as ScheduleSummary & { isFixedTime: boolean };
        if (row.isFixedTime) {
          return <Typography variant="body2">NA</Typography>;
        }
        const days = params.value as number[];
        if (!days || days.length === 0 || days.length === 7) {
          return <Typography variant="body2">Every day</Typography>;
        }
        return <DayOfWeekCell days={days} />;
      },
    },
    {
      field: 'frequency',
      headerName: 'Frequency',
      flex: 1,
      minWidth: 180,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ScheduleSummary & { frequencyTooltip?: string };
        const value = (params.value as string) || '-';
        const tooltipTitle = row.frequencyTooltip ?? value;

        return (
          <Tooltip title={tooltipTitle}>
            <Typography
              variant="body2"
              noWrap
              sx={{ cursor: row.frequencyTooltip ? 'help' : 'default' }}
            >
              {value}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'nextRun',
      headerName: `Next Run${timeMode === 'utc' ? ' (UTC)' : ''}`,
      width: 190,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ScheduleSummary & { isFixedTime: boolean };
        if (row.isSchedulesPaused) {
          return <Chip label="Paused" size="small" color="warning" />;
        }
        if (row.isFixedTime && !params.value) {
          return <Typography variant="body2">NA</Typography>;
        }
        const formatted = params.value
          ? formatScheduleApiDateTime(params.value as string, row, 'nextRun', timeMode)
          : '-';
        if (formatted === '-' && params.value) {
          // Fixed-time schedules are one-shot runs — once the date passes there
          // is no "next" run by design, so "Expired" is misleading; show NA.
          if (row.isFixedTime) {
            return <Typography variant="body2">NA</Typography>;
          }
          return (
            <Tooltip title="This schedule has no future runs. Edit to reschedule.">
              <Chip label="Expired" size="small" color="default" variant="outlined" />
            </Tooltip>
          );
        }
        return <Typography variant="body2">{formatted}</Typography>;
      },
    },
    {
      field: 'lastRun',
      headerName: `Last Run${timeMode === 'utc' ? ' (UTC)' : ''}`,
      width: 190,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ScheduleSummary;
        return (
          <Typography variant="body2">
            {params.value
              ? formatScheduleApiDateTime(params.value as string, row, 'lastRun', timeMode)
              : '-'}
          </Typography>
        );
      },
    },
    {
      field: 'lastRunDuration',
      headerName: 'Duration',
      width: 100,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">{formatDuration(params.value)}</Typography>
      ),
    },
    {
      field: 'executionStatus',
      headerName: 'Status',
      width: 110,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ScheduleSummary;
        return (
          <StatusCell
            status={params.value}
            externalExecutionId={row.externalExecutionId}
            environmentId={String(row.environmentId)}
            appId={appId}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onEdit?.(params.row as ScheduleSummary);
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const handleRowClick = (params: GridRowParams) => {
    onRowClick?.(params.row as ScheduleSummary);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Toolbar with refresh and UTC / Local toggle */}
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1 }}
      >
        {showTimeToggle && <TimeDisplayToggle value={timeMode} onChange={setTimeMode} />}
        {onRefresh && (
          <Tooltip title="Refresh">
            <Box component="span" sx={{ display: 'inline-block' }}>
              <IconButton size="small" onClick={onRefresh} disabled={isLoading}>
                <RefreshIcon
                  fontSize="small"
                  sx={{
                    animation: isLoading ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              </IconButton>
            </Box>
          </Tooltip>
        )}
      </Stack>

      <DataGrid
        rows={enrichedSchedules}
        columns={columns}
        loading={isLoading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 20, 50]}
        disableRowSelectionOnClick
        onRowClick={handleRowClick}
        autoHeight
        sx={{
          '& .MuiDataGrid-row': {
            cursor: onRowClick ? 'pointer' : 'default',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton',
          },
        }}
      />
    </Box>
  );
};
